import { Temporal } from '../../Temporal';

export interface Type<T, N extends string = string> {
  name: N;
  is: (x: unknown) => x is T;
}

const typeFrom = <T, N extends string>(name: N, is: (x: unknown) => x is T): Type<T, N> => ({
  name,
  is,
});

const booleanType = typeFrom('boolean', (x): x is boolean => typeof x === 'boolean');
const numberType = typeFrom('number', (x): x is number => typeof x === 'number');
const stringType = typeFrom('string', (x): x is string => typeof x === 'string');
const dateType = typeFrom('date', (x): x is Date => x instanceof Date);
const durationType = typeFrom('duration', (x): x is Temporal.Duration => x instanceof Temporal.Duration);
const zonedDateTimeType = typeFrom(
  'zonedDateTime',
  (x): x is Temporal.ZonedDateTime => x instanceof Temporal.ZonedDateTime,
);
const plainDateType = typeFrom('plainDate', (x): x is Temporal.PlainDate => x instanceof Temporal.PlainDate);
const plainTimeType = typeFrom('plainTime', (x): x is Temporal.PlainTime => x instanceof Temporal.PlainTime);

export const TYPES = {
  [booleanType.name]: booleanType,
  [numberType.name]: numberType,
  [stringType.name]: stringType,
  [dateType.name]: dateType,
  [durationType.name]: durationType,
  [zonedDateTimeType.name]: zonedDateTimeType,
  [plainDateType.name]: plainDateType,
  [plainTimeType.name]: plainTimeType,
} as const satisfies Record<string, Type<unknown>>;

export type ExpressionType = keyof typeof TYPES;

export type TypeFrom<T extends Type<unknown>> = T extends Type<infer U> ? U : never;

export type TypeMap = {
  [k in keyof typeof TYPES]: TypeFrom<(typeof TYPES)[k]>;
};
